import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, CheckCircle2, Loader2, Smartphone } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/atoms/alert';
import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Skeleton } from '@/components/atoms/skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { publicContactPhoneLinkEndpoints } from '@/lib/api/endpoints';
import { publicGet, publicPost } from '@/lib/api/publicClient';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';

/**
 * The inbox owner's side of "is this your phone number?".
 *
 * Someone texting the business gave this email as theirs. Iris never links a
 * phone to a contact on the customer's say-so (that would let anyone who knows
 * an email inherit its owner's account), so the owner is asked here, on the
 * channel that proves it. The token in the URL carries everything; there is no
 * session and no tenant header.
 */
export const Route = createFileRoute('/(public)/link-phone/$token')({
  component: LinkPhonePage,
});

interface PhoneLinkDetails {
  tenantName: string;
  firstName: string | null;
  phoneMasked: string;
  alreadyLinked: boolean;
}

interface PhoneLinkConfirmResult {
  contactId: string;
  linked: true;
  outcome: 'merged' | 'phone_added' | 'already_linked';
}

function LinkPhonePage() {
  const { token } = Route.useParams();
  const details = useApiQuery<PhoneLinkDetails>({
    queryKey: ['public-contact-phone-link', token],
    queryFn: () => publicGet<PhoneLinkDetails>(publicContactPhoneLinkEndpoints.detail(token)),
    enabled: Boolean(token),
    showErrorToast: false,
  });
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const confirm = async () => {
    setConfirming(true);
    setConfirmError(null);
    try {
      await publicPost<PhoneLinkConfirmResult>(publicContactPhoneLinkEndpoints.confirm(token));
      setConfirmed(true);
    } catch {
      setConfirmError('We could not complete this right now. Please try the link again later.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <PublicCenteredPage className="bg-muted/30">
      <Card className="w-full max-w-md">
        {details.isLoading ? (
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        ) : details.isError || !details.data ? (
          <CardContent className="pt-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>This link is invalid or has expired</AlertTitle>
              <AlertDescription>
                Nothing has changed on your account. If you did want to add a phone number, reply to
                the business from that number and we will send a fresh link.
              </AlertDescription>
            </Alert>
          </CardContent>
        ) : confirmed || details.data.alreadyLinked ? (
          <>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Phone number linked
              </CardTitle>
              <CardDescription>
                {details.data.phoneMasked} is now on your {details.data.tenantName} account. Your
                bookings and messages from that number will show up in one place.
              </CardDescription>
            </CardHeader>
          </>
        ) : (
          <>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                Is this your phone number?
              </CardTitle>
              <CardDescription>
                {details.data.firstName ? `Hi ${details.data.firstName}, s` : 'S'}omeone texting{' '}
                {details.data.tenantName} from <strong>{details.data.phoneMasked}</strong> gave this
                email address as theirs.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-sm">
                If that was you, confirm below and we will add the number to your account. If you
                don&apos;t recognise it, just close this page — nothing will change.
              </p>
              {confirmError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{confirmError}</AlertDescription>
                </Alert>
              ) : null}
              <Button className="w-full" onClick={confirm} disabled={confirming}>
                {confirming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Yes, that&apos;s my number
              </Button>
            </CardContent>
          </>
        )}
      </Card>
    </PublicCenteredPage>
  );
}
